import { Modal, Button as ButtonLib, Alert, notification } from 'antd';
import { DateTime } from 'luxon';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PhoneInput from 'react-phone-number-input';
import validator from 'validator';
import { RefFormHandler } from '../../pages/orders/details';
import { Role } from '../../services/auth-service/types';
import { AdminSignupBody, useAuthStore } from '../../store/auth-store';
import { FieldType } from '../../types';
import Form from '../Form';
import { Button } from '../inputs/button';

/**
 * AddDashboardUser props
 */
interface Props {
  open?: boolean;
  onUserCreated?: () => void;
}

/**
 * AddDashboardUser Component
 */
const AddDashboardUser: FunctionComponent<Props> = ({
  open,
  onUserCreated,
}) => {
  const [isOpen, setIsOpen] = useState(open || false);
  const formRef = useRef<RefFormHandler>(null);
  const { adminSignup, adminSignUpState, idToken } = useAuthStore();
  const [api, contextHolder] = notification.useNotification();

  const onClose = useCallback(() => setIsOpen(false), []);

  const onSubmitCapture = useCallback(
    async (data: unknown) => {
      const dataWithType = data as AdminSignupBody;
      await adminSignup({
        ...dataWithType,
        birthDate: DateTime.fromJSDate(
          new Date(dataWithType.birthDate),
        ).toFormat('LL/dd/yyyy'),
      });
      api.success({
        message: 'Success',
        description: 'Admin account created.',
        placement: 'topRight',
      });
      setIsOpen(false);
      if (onUserCreated) {
        onUserCreated();
      }
    },
    [adminSignup, api, onUserCreated],
  );

  useEffect(() => {
    if (!isOpen) {
      if (formRef.current?.reset) formRef.current?.reset();
    }
  }, [isOpen]);

  const cUser = useMemo(() => idToken?.payload, [idToken]);
  const canAddDashboardUser = useMemo(() => {
    if (!cUser?.['custom:roles']) return false;
    const roles = cUser['custom:roles'].split(',') as Role[];
    const rolesAccess = [Role.SuperUser];
    let canAccess = false;
    roles.forEach(role => {
      if (rolesAccess.includes(role)) {
        canAccess = true;
      }
    });
    return canAccess;
  }, [cUser]);

  return canAddDashboardUser ? (
    <>
      {contextHolder}
      <Button onClick={() => setIsOpen(true)}>Add Dashboard User</Button>
      <Modal
        open={isOpen}
        width={900}
        footer={null}
        maskClosable={false}
        closable={false}>
        {adminSignUpState.error && (
          <Alert message={adminSignUpState.error} type="error" />
        )}
        <Form
          ref={formRef}
          fieldOptions={{ className: 'md:basis-1/2' }}
          formProps={{ onSubmitCapture }}
          fields={[
            {
              key: 'email',
              required: true,
              inputProps: { type: 'email' },
              rules: {
                validate: val => {
                  if (!validator.isEmail(val)) {
                    return 'Email is not valid.';
                  }
                  return undefined;
                },
              },
            },
            {
              key: 'role',
              label: 'User Type',
              type: FieldType.Select,
              required: true,
              options: [
                { label: 'Admin', value: 'admin,user' },
                { label: 'Physician', value: 'physician,user' },
                { label: 'Event Manager', value: 'eventmanager,user' },
              ],
            },
            { key: 'firstName', required: true, className: 'md:basis-1/3' },
            { key: 'middleName', required: false, className: 'md:basis-1/3' },
            { key: 'lastName', required: true, className: 'md:basis-1/3' },
            { key: 'birthDate', type: FieldType.Date, required: false },
            {
              key: 'gender',
              type: FieldType.Select,
              options: [
                { value: 'M', label: 'Male' },
                { value: 'F', label: 'Female' },
              ],
            },

            {
              key: 'phone',
              label: 'Contact Number',
              render({ value, onChange }) {
                return (
                  <PhoneInput
                    className="flex-1 border border-gray rounded-md p-2 placeholder:text-gray-medium bg-white"
                    defaultCountry="US"
                    value={value}
                    onChange={onChange}
                  />
                );
              },
              type: FieldType.Custom,
            },
          ]}
          hasSubmitButton={false}
        />
        <span className="text-base text-red">* required</span>
        <div className="flex justify-end mt-2 gap-2">
          <ButtonLib onClick={onClose} loading={adminSignUpState.isLoading}>
            Cancel
          </ButtonLib>
          <ButtonLib
            loading={adminSignUpState.isLoading}
            type="primary"
            onClick={data => {
              if (formRef) {
                formRef.current?.onSubmit(data);
              }
            }}>
            Add User
          </ButtonLib>
        </div>
      </Modal>
    </>
  ) : null;
};

export default AddDashboardUser;
